import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';

import i18n from '@/shared/i18n';
import { bindSessionToTokenEvents } from '@/shared/store/sessionStore';
import { applyTheme, useThemeStore } from '@/shared/store/themeStore';
import { ToastHost } from '@/shared/ui';

import { ErrorBoundary } from '../ErrorBoundary';

import { createQueryClient } from './queryClient';

/**
 * Applies the persisted theme and keeps <html lang> in sync.
 *
 * The inline script in index.html already set `data-theme` before first paint;
 * this re-applies it from the store so a change made in one tab does not get
 * reverted on the next render.
 */
function useAppSideEffects(): void {
  const preference = useThemeStore((state) => state.preference);

  useEffect(() => {
    applyTheme(preference);
  }, [preference]);

  useEffect(() => bindSessionToTokenEvents(), []);
}

function SideEffects() {
  useAppSideEffects();
  return null;
}

export function AppProviders({
  children,
  queryClient: provided
}: {
  children: ReactNode;
  /**
   * Test seam. Supplying the client lets a test INSPECT the real cache rather
   * than assume what is in it — which is how "no card number is ever cached" is
   * asserted against the actual store the app writes to, instead of against a
   * separate one built for the assertion.
   */
  queryClient?: QueryClient;
}) {
  // One client per app instance; recreating it on render would drop the cache.
  const created = useMemo(() => createQueryClient(), []);
  const queryClient = provided ?? created;

  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <SideEffects />
          {children}
          <ToastHost />
        </QueryClientProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}
